"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DatePicker from "@/components/form/date-picker";
import Pagination from "@/components/tables/Pagination";
import { Modal } from "@/components/ui/modal/index";
import ChallanPrint from "@/components/print/ChallanPrint";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import React, { useRef, useState } from "react";
import toast from "react-hot-toast";

function fmt(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

const PAGE_SIZE = 20;

/** DRF-style list payload; supports `meta` block (count, next, previous) and top-level fields. */
function readPaginatedListMeta(raw) {
  if (!raw || typeof raw !== "object") {
    return { count: null, hasNext: false, hasPrevious: false };
  }
  const meta = raw.meta && typeof raw.meta === "object" ? raw.meta : null;
  const tryCount = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    for (const key of ["count", "total", "total_count"]) {
      const n = Number(obj[key]);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return null;
  };
  const count = tryCount(raw) ?? tryCount(raw.data) ?? tryCount(meta);
  return {
    count,
    hasNext: Boolean(raw.next ?? meta?.next),
    hasPrevious: Boolean(raw.previous ?? meta?.previous),
  };
}

/** Delivery sites from sales parties for filters (searchable dropdown). */
function buildSalesSiteOptions(parties, partyFilterId) {
  const rows = [];
  const seen = new Set();
  for (const p of parties || []) {
    if (
      partyFilterId !== undefined &&
      partyFilterId !== null &&
      partyFilterId !== "" &&
      String(p.id) !== String(partyFilterId)
    ) {
      continue;
    }
    for (const s of p.sites || []) {
      const name = (s.name || "").trim();
      if (!name) continue;
      const id = s.id != null && s.id !== "" ? String(s.id) : `p${p.id}::${name}`;
      if (seen.has(id)) continue;
      seen.add(id);
      rows.push({
        id,
        name,
        siteName: name,
        partyName: (p.name || "").trim(),
      });
    }
  }
  rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  return [{ id: "", name: "All sites", siteName: "", partyName: "" }, ...rows];
}

function challanGrade(row) {
  const items = Array.isArray(row?.items) ? row.items : [];
  const names = items
    .map((it) => it?.grade?.name || it?.grade_name || "")
    .filter(Boolean);
  if (!names.length) return "—";
  const unique = Array.from(new Set(names));
  return unique.join(", ");
}

function challanQty(row) {
  const items = Array.isArray(row?.items) ? row.items : [];
  if (items.length) {
    const total = items.reduce((sum, it) => {
      const qty = Number(it?.qty_cum ?? it?.qty);
      return Number.isFinite(qty) ? sum + qty : sum;
    }, 0);
    if (Number.isFinite(total) && total > 0) return total;
  }
  const topLevelQty = Number(row?.qty_cum ?? row?.qty);
  if (Number.isFinite(topLevelQty)) return topLevelQty;
  return NaN;
}

export default function RmcSalesChallansList() {
  const qc = useQueryClient();
  const isoToday = React.useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);
  const [detailId, setDetailId] = useState(null);
  const [printRow, setPrintRow] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [filters, setFilters] = useState({
    date_from: isoToday,
    date_to: isoToday,
    party_id: "",
    vehicle_id: "",
    tax_type: "",
    site_id: "",
  });
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const printRef = useRef(null);

  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });
  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles"],
    queryFn: async () => (await rmcClient.listVehicles()).data,
  });

  const siteOptions = React.useMemo(
    () => buildSalesSiteOptions(parties, filters.party_id),
    [parties, filters.party_id]
  );

  const siteNameForApi = React.useMemo(() => {
    if (!filters.site_id) return "";
    const o = siteOptions.find((x) => String(x.id) === String(filters.site_id));
    return (o?.siteName || "").trim();
  }, [filters.site_id, siteOptions]);

  React.useEffect(() => {
    if (!filters.site_id) return;
    const ok = siteOptions.some((o) => o.id !== "" && String(o.id) === String(filters.site_id));
    if (!ok) setFilters((prev) => ({ ...prev, site_id: "" }));
  }, [siteOptions, filters.site_id]);

  const queryParams = React.useMemo(
    () => ({
      ...(filters.party_id ? { party_id: filters.party_id } : {}),
      ...(filters.vehicle_id ? { vehicle_id: filters.vehicle_id } : {}),
      ...(filters.tax_type ? { tax_type: filters.tax_type } : {}),
      ...(filters.date_from ? { date_from: filters.date_from } : {}),
      ...(filters.date_to ? { date_to: filters.date_to } : {}),
      ...(siteNameForApi ? { site_name: siteNameForApi } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [
      filters.party_id,
      filters.vehicle_id,
      filters.tax_type,
      filters.date_from,
      filters.date_to,
      siteNameForApi,
      page,
    ]
  );
  const { data: challansData, isFetching } = useQuery({
    queryKey: ["salesChallans", queryParams],
    queryFn: async () => (await rmcClient.listSalesChallans(queryParams)).data,
    keepPreviousData: true,
  });
  const rows = React.useMemo(() => {
    if (Array.isArray(challansData)) return challansData;
    if (Array.isArray(challansData?.results)) return challansData.results;
    if (Array.isArray(challansData?.data?.results)) return challansData.data.results;
    return [];
  }, [challansData]);

  const listResponseMeta = React.useMemo(() => {
    if (!challansData || typeof challansData !== "object" || Array.isArray(challansData)) return null;
    const m = challansData.meta ?? challansData.data?.meta;
    return m && typeof m === "object" ? m : null;
  }, [challansData]);

  const totalQtyCumFromMeta = React.useMemo(() => {
    if (!listResponseMeta) return null;
    const raw = listResponseMeta.total_qty_cum ?? listResponseMeta.total_qty_cm;
    if (raw == null || String(raw).trim() === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [listResponseMeta]);

  const paginationMeta = React.useMemo(() => readPaginatedListMeta(challansData), [challansData]);

  const totalCount = React.useMemo(() => {
    if (paginationMeta.count != null) return paginationMeta.count;
    if (paginationMeta.hasNext) {
      return (page - 1) * PAGE_SIZE + rows.length + 1;
    }
    return (page - 1) * PAGE_SIZE + rows.length;
  }, [paginationMeta, page, rows.length]);

  const totalPages = React.useMemo(() => {
    if (paginationMeta.count != null) {
      return Math.max(1, Math.ceil(paginationMeta.count / PAGE_SIZE));
    }
    if (paginationMeta.hasNext) {
      return Math.max(page + 1, 1);
    }
    return Math.max(1, page);
  }, [paginationMeta, page]);

  React.useEffect(() => {
    setPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  const currentPage = Math.min(page, totalPages);
  const activeFilterCount = React.useMemo(
    () =>
      [
        filters.date_from,
        filters.date_to,
        filters.party_id,
        filters.vehicle_id,
        filters.tax_type,
        filters.site_id,
      ].filter(Boolean).length,
    [filters]
  );
  const { data: detail, isLoading: isDetailLoading } = useQuery({
    queryKey: ["salesChallan", detailId],
    queryFn: async () => (await rmcClient.getSalesChallan(detailId)).data,
    enabled: detailId != null,
  });

  const remove = useMutation({
    mutationFn: (id) => rmcClient.deleteSalesChallan(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["salesChallans"] });
      qc.invalidateQueries({ queryKey: ["materials"] });
      qc.removeQueries({ queryKey: ["salesChallan", id] });
      setDeleteId(null);
      setDetailId((prev) => (prev === id ? null : prev));
      toast.success("Sales challan deleted");
    },
    onError: () => toast.error("Failed to delete challan."),
  });

  const pn = React.useCallback((id) => parties.find((p) => p.id === id)?.name || "—", [parties]);
  const siteName = React.useCallback(
    (partyId, siteId) => {
      const p = parties.find((x) => x.id === partyId);
      return p?.sites?.find((s) => s.id === siteId)?.name || "—";
    },
    [parties]
  );
  const veh = React.useCallback(
    (id) => {
      const v = vehicles.find((x) => x.id === id);
      return v ? `${v.registration_no}${v.alias ? ` (${v.alias})` : ""}` : "—";
    },
    [vehicles]
  );
  const deleteRow = rows.find((r) => r.id === deleteId);
  const taxTypeOptions = React.useMemo(
    () => [
      { id: "", name: "All" },
      { id: "GST", name: "GST" },
      { id: "NON_GST", name: "Non-GST" },
    ],
    []
  );

  const updateFilters = React.useCallback((next) => {
    setFilters((prev) => {
      const merged = { ...prev, ...next };
      if ("party_id" in next && next.party_id !== prev.party_id) {
        merged.site_id = "";
      }
      return merged;
    });
    setPage(1);
  }, []);

  const clearFilters = React.useCallback(() => {
    setFilters({
      date_from: isoToday,
      date_to: isoToday,
      party_id: "",
      vehicle_id: "",
      tax_type: "",
      site_id: "",
    });
    setPage(1);
  }, [isoToday]);

  const exportFiltered = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const payload = (
        await rmcClient.listSalesChallans({
          ...(filters.party_id ? { party_id: filters.party_id } : {}),
          ...(filters.vehicle_id ? { vehicle_id: filters.vehicle_id } : {}),
          ...(filters.tax_type ? { tax_type: filters.tax_type } : {}),
          ...(filters.date_from ? { date_from: filters.date_from } : {}),
          ...(filters.date_to ? { date_to: filters.date_to } : {}),
          ...(siteNameForApi ? { site_name: siteNameForApi } : {}),
          pagination: false,
        })
      ).data;
      const exportRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data?.results)
            ? payload.data.results
            : [];
      const header = [
        "challan_no",
        "date",
        "party",
        "grade",
        "qty_cum",
        "site",
        "vehicle",
        "tax_type",
        "gst_rate",
        "total_amount",
      ];
      const lines = exportRows.map((r) =>
        [
          r.challan_no ?? "",
          r.date ?? "",
          pn(r.party_id),
          challanGrade(r),
          Number.isFinite(challanQty(r)) ? challanQty(r) : "",
          siteName(r.party_id, r.site_id),
          veh(r.vehicle_id),
          r.tax_type ?? "",
          r.gst_rate ?? "",
          r.total_amount ?? "",
        ]
          .map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`)
          .join(",")
      );
      const csv = [header.join(","), ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales-challans-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${exportRows.length} challans`);
    } catch {
      toast.error("Failed to export challans.");
    } finally {
      setIsExporting(false);
    }
  }, [
    filters.party_id,
    filters.vehicle_id,
    filters.tax_type,
    filters.date_from,
    filters.date_to,
    siteNameForApi,
    pn,
    siteName,
    veh,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-3">
        <PageBreadCrumb pageTitle="Sales Challans" />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isExporting}
            onClick={exportFiltered}
          >
            {isExporting ? "Exporting…" : "Export All Filtered"}
          </Button>
          <Link
            href="/sales/invoices"
            className="inline-flex items-center justify-center rounded-md border border-gray-700 bg-transparent px-3 py-1.5 text-xs font-medium text-gray-200 hover:bg-white/5"
          >
            Invoices
          </Link>
          <Link
            href="/sales/challans/bulk"
            className="inline-flex items-center justify-center rounded-md border border-gray-700 bg-transparent px-3 py-1.5 text-xs font-medium text-gray-200 hover:bg-white/5"
          >
            Bulk Create
          </Link>
          <Link
            href="/sales/challans/new"
            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-1.5 text-xs font-medium text-black shadow-theme-xs hover:bg-gray-200"
          >
            New
          </Link>
        </div>
      </div>
      <div className="rounded-2xl border border-gray-200/80 bg-white/90 p-4 shadow-lg shadow-gray-200/50 ring-1 ring-gray-100/80 backdrop-blur-sm transition-all dark:border-gray-800 dark:bg-gray-900/50 dark:shadow-black/30 dark:ring-gray-800/80">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Smart Filters
            </p>
            {activeFilterCount > 0 && (
              <span className="menu-dropdown-badge-active rounded-full px-2.5 py-1 text-[11px] font-semibold">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {rows.length} of {totalCount} records
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date From</label>
            <DatePicker
              id="sales-challan-filter-from"
              defaultDate={filters.date_from}
              placeholder="From date"
              onChange={(_, dateStr) => updateFilters({ date_from: dateStr || "" })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date To</label>
            <DatePicker
              id="sales-challan-filter-to"
              defaultDate={filters.date_to}
              placeholder="To date"
              onChange={(_, dateStr) => updateFilters({ date_to: dateStr || "" })}
            />
          </div>
          <SearchableSelect
            label="Party"
            value={filters.party_id}
            onChange={(v) => updateFilters({ party_id: v || "" })}
            options={[{ id: "", name: "All parties" }, ...parties]}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            placeholder="All parties"
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
          <SearchableSelect
            label="Vehicle"
            value={filters.vehicle_id}
            onChange={(v) => updateFilters({ vehicle_id: v || "" })}
            options={[{ id: "", registration_no: "All vehicles", alias: "" }, ...vehicles]}
            getLabel={(o) => o.registration_no || o.name}
            getValue={(o) => o.id}
            getSubLabel={(o) => o.alias || ""}
            placeholder="All vehicles"
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
          <SearchableSelect
            label="Tax Type"
            value={filters.tax_type}
            onChange={(v) => updateFilters({ tax_type: v || "" })}
            options={taxTypeOptions}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            placeholder="All"
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
          <SearchableSelect
            label="Site"
            value={filters.site_id}
            onChange={(v) => updateFilters({ site_id: v || "" })}
            options={siteOptions}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            getSubLabel={(o) => (o.partyName && !filters.party_id ? o.partyName : undefined)}
            placeholder="All sites"
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200/70 pt-3 dark:border-gray-800">
          <Button type="button" size="sm" variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </div>

      {totalQtyCumFromMeta != null && (
          <div className="rounded-2xl border border-gray-200/80 bg-white/90 px-5 py-4 shadow-lg shadow-gray-200/50 ring-1 ring-gray-100/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/50 dark:shadow-black/30 dark:ring-gray-800/80">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
              Total quantity (CUM)
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-gray-900 dark:text-white">
              {fmt(totalQtyCumFromMeta, 3)}
            </p>
          </div>
        )}

      <DataTable
        columns={[
          { key: "no", header: "Challan No", render: (r) => r.challan_no },
          {key: "batch",header:"Batch No", render:(r) => r.batch_no},
          { key: "date", header: "Date" },
          { key: "party", header: "Party", render: (r) => pn(r.party_id) },
          { key: "grade", header: "Grade", render: (r) => challanGrade(r) },
          {
            key: "qty",
            header: "Qty (CUM)",
            render: (r) => {
              const qty = challanQty(r);
              return Number.isFinite(qty) ? fmt(qty, 3) : "—";
            },
          },
          {
            key: "tax",
            header: "Tax",
            render: (r) =>
              r.tax_type === "GST"
                ? `GST${r.gst_rate ? ` (${Number(r.gst_rate).toFixed(2)}%)` : ""}`
                : "Non-GST",
          },
          { key: "site", header: "Site", render: (r) => siteName(r.party_id, r.site_id) },
          { key: "veh", header: "Vehicle", render: (r) => veh(r.vehicle_id) },
          {
            key: "tot",
            header: "Total",
            render: (r) => `₹${fmt(r.total_amount, 0)}`,
          },
          {
            key: "act",
            header: "Actions",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (r) => (
              <div className="flex w-full justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPrintRow(r);
                    setTimeout(() => printRef.current?.openPrint?.(), 200);
                  }}
                >
                  Print
                </Button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={isFetching}
        onRowClick={(r) => setDetailId(r.id)}
      />
      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
        />
      </div>

      <Modal
        isOpen={detailId != null}
        onClose={() => setDetailId(null)}
        className="max-w-4xl w-full max-h-[min(90vh,760px)] overflow-y-auto"
      >
        {isDetailLoading ? (
          <div className="p-6 text-sm text-gray-400">Loading challan…</div>
        ) : detail ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-800 pb-4 pr-8">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Sales Challan
                </p>
                <h2 className="mt-1 text-xl font-semibold text-white">{detail.challan_no}</h2>
                <p className="mt-1 text-sm text-gray-400">Date: {detail.date}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setPrintRow(detail);
                    setTimeout(() => printRef.current?.openPrint?.(), 200);
                  }}
                >
                  Print
                </Button>
                <Link
                  href={`/sales/challans/${detail.id}/edit`}
                  onClick={() => setDetailId(null)}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-xs font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  Edit
                </Link>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={remove.isPending}
                  onClick={() => setDeleteId(detail.id)}
                >
                  Delete
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-800">
              <table className="w-full text-left text-xs">
                <tbody className="divide-y divide-gray-800">
                  <tr className="bg-gray-950/60">
                    <th className="w-40 px-3 py-2.5 font-semibold text-gray-500">Party</th>
                    <td className="px-3 py-2.5 text-gray-200">{pn(detail.party_id)}</td>
                  </tr>
                  <tr>
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Site</th>
                    <td className="px-3 py-2.5 text-gray-200">{siteName(detail.party_id, detail.site_id)}</td>
                  </tr>
                  <tr className="bg-gray-950/40">
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Vehicle</th>
                    <td className="px-3 py-2.5 text-gray-200">{veh(detail.vehicle_id)}</td>
                  </tr>
                  <tr>
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Driver</th>
                    <td className="px-3 py-2.5 text-gray-200">{detail.driver_name || "—"}</td>
                  </tr>
                  <tr className="bg-gray-950/40">
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Order By</th>
                    <td className="px-3 py-2.5 text-gray-200">{detail.order_by || "—"}</td>
                  </tr>
                  <tr>
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Batch No</th>
                    <td className="px-3 py-2.5 text-gray-200">{detail.batch_no || "—"}</td>
                  </tr>
                  <tr>
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Tax Type</th>
                    <td className="px-3 py-2.5 text-gray-200">
                      {detail.tax_type === "GST" ? "GST" : "Non-GST"}
                    </td>
                  </tr>
                  <tr className="bg-gray-950/40">
                    <th className="px-3 py-2.5 font-semibold text-gray-500">GST Rate</th>
                    <td className="px-3 py-2.5 text-gray-200">
                      {detail.tax_type === "GST"
                        ? `${Number(detail.gst_rate || 0).toFixed(2)}%`
                        : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="rounded-lg border border-gray-800 overflow-hidden">
              <div className="border-b border-gray-800 bg-gray-950/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Line Items
              </div>
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-950/60">
                    <th className="px-3 py-2 font-semibold text-gray-500">Grade</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500">Qty (CUM)</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500">Rate (₹)</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500">Amount (₹)</th>
                    <th className="px-3 py-2 font-semibold text-gray-500">Remark</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {(detail.items || []).map((it, idx) => (
                    <tr key={idx} className="bg-gray-950/20">
                      <td className="px-3 py-2 text-gray-200">{it.grade?.name || `Grade #${idx + 1}`}</td>
                      <td className="px-3 py-2 text-right text-gray-200">
                        {fmt(it.qty_cum ?? it.qty, 3)}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-200">{fmt(it.rate, 2)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-white">
                        {fmt((Number(it.qty_cum ?? it.qty) || 0) * (Number(it.rate) || 0), 2)}
                      </td>
                      <td className="px-3 py-2 text-gray-400">{it.remark || "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {detail.tax_type === "GST" && (
                    <tr className="border-t border-gray-800 bg-gray-950/70">
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-400" colSpan={3}>
                        Subtotal
                      </td>
                      <td className="px-3 py-2.5 text-right font-semibold text-white">
                        ₹
                        {fmt(
                          (detail.items || []).reduce(
                            (sum, it) =>
                              sum + (Number(it.qty_cum ?? it.qty) || 0) * (Number(it.rate) || 0),
                            0
                          ),
                          2
                        )}
                      </td>
                      <td />
                    </tr>
                  )}
                  {detail.tax_type === "GST" && (
                    <tr className="border-t border-gray-800 bg-gray-950/70">
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-400" colSpan={3}>
                        GST ({Number(detail.gst_rate || 0).toFixed(2)}%)
                      </td>
                      <td className="px-3 py-2.5 text-right font-semibold text-white">
                        ₹
                        {fmt(
                          ((detail.items || []).reduce(
                            (sum, it) =>
                              sum + (Number(it.qty_cum ?? it.qty) || 0) * (Number(it.rate) || 0),
                            0
                          ) *
                            Number(detail.gst_rate || 0)) /
                            100,
                          2
                        )}
                      </td>
                      <td />
                    </tr>
                  )}
                  <tr className="border-t border-gray-800 bg-gray-950/90">
                    <td className="px-3 py-2.5 text-right font-semibold text-gray-400" colSpan={3}>
                      Total
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-white">
                      ₹{fmt(detail.total_amount, 2)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={deleteId != null}
        onClose={() => {
          if (!remove.isPending) setDeleteId(null);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete sales challan?</h3>
          <p className="text-sm leading-relaxed text-gray-400">
            {deleteRow?.challan_no ? (
              <>
                This will permanently remove{" "}
                <span className="font-medium text-gray-200">{deleteRow.challan_no}</span>. Stock and reports may
                adjust accordingly.
              </>
            ) : (
              <>This will permanently remove the challan. Stock and reports may adjust accordingly.</>
            )}
          </p>
          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-800 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={remove.isPending}
              onClick={() => setDeleteId(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={remove.isPending}
              onClick={() => deleteId != null && remove.mutate(deleteId)}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>

      <ChallanPrint ref={printRef} challan={printRow} />
    </div>
  );
}

